/**
 * Component-specific style collections for the zkML Perplexity UI
 * 
 * This file contains predefined style combinations for specific components
 * to further reduce inline CSS and standardize component appearance.
 */

import { typography, layout, animations, forms, buttons, cx } from './index';

// Common UI elements
export const common = {
  // Section headers - used across different components
  sectionHeader: cx(
    typography.size.xs, 
    typography.weight.medium, 
    'text-secondary', 
    'mb-2'
  )
};

// Header components
export const header = {
  main: cx(
    layout.flex.between,
    'bg-component', 
    'border-b', 
    'border-main',
    'px-3 py-2'
  ),
  title: typography.styles.heading,
};

// Search components
export const search = {
  // Search wrapper
  container: cx(
    'relative',
    animations.transition.default
  ),
  // Input field
  input: cx(
    forms.input,
    forms.inputShapes.pill,
    'w-full h-10 sm:h-11 md:h-12 pl-4 pr-11',
    'text-sm',
    'border-contrast',
    'shadow-sm',
    'bg-contrast'
  ),
  // Search button
  button: cx(
    'absolute right-2 top-1/2 transform -translate-y-1/2',
    'bg-accent-main',
    'text-primary-inverse',
    'hover:bg-accent',
    'p-1.5 rounded-full', 
    animations.transition.color
  ),
  // Helper text
  helperText: cx(
    'text-center mt-2',
    typography.size.sm, 
    'text-tertiary'
  ),
  // Focus state
  focusedContainer: 'scale-[1.01]'
};

// Topic styling - Updated for light background visibility
export const topics = {
  // Section header (using common section header)
  sectionHeader: common.sectionHeader,
  // Container for topics
  container: 'flex flex-wrap gap-1.5 mt-1',
  // Individual topic chip
  chip: cx(
    'px-2.5 py-1 rounded-full', 
    'bg-component', 
    'text-secondary',
    'hover:bg-component-light', 
    animations.transition.color,
    typography.size.xs, 
    typography.weight.medium,
    'border', 
    'border-main'
  ),
  // Topic count badge
  count: cx(
    'ml-0.5',
    typography.size.tiny,
    'text-accent'
  )
};

// Card styling for content items
export const cards = {
  // Section header (using common section header)
  sectionHeader: common.sectionHeader,
  // Container for cards
  grid: 'grid grid-cols-1 md:grid-cols-2 gap-2', 
  // Card container
  item: cx(
    'bg-component', 
    'border', 
    'border-main', 
    'rounded-md overflow-hidden shadow-sm',
    'hover:shadow', 
    'hover:border-accent', 
    'transition-all cursor-pointer',
    'flex items-center p-2'
  ),
  // Card image
  image: 'flex-shrink-0 w-9 h-9 mr-2',
  imgContent: 'w-full h-full object-cover rounded-md',
  // Card content
  content: 'flex-grow min-w-0',
  titleRow: 'flex justify-between items-start',
  title: cx(
    'text-xs font-medium',
    'text-secondary',
    'truncate'
  ),
  // Card badges
  researchBadge: cx(
    'ml-1 px-1.5 py-0.5',
    typography.size.tiny,
    typography.weight.medium,
    'rounded-full flex-shrink-0',
    'bg-accent-dark',
    'text-primary-inverse' // Ensure readability on dark background
  ),
  productBadge: cx(
    'ml-1 px-1.5 py-0.5',
    typography.size.tiny,
    typography.weight.medium,
    'rounded-full flex-shrink-0',
    'bg-success',
    'text-primary-inverse' // Ensure readability on dark background
  ),
  description: cx(
    'text-[11px]',
    'text-tertiary',
    'line-clamp-1',
    'mt-0.5'
  )
};

// Sidebar styling
export const sidebar = {
  // Base container
  container: cx(
    'h-screen',
    'bg-page',
    'text-primary',
    'flex flex-col'
  ),
  // Desktop-specific styles
  desktop: 'w-64',
  // Mobile-specific styles
  mobile: cx(
    'fixed left-0 top-0 z-50 w-64',
    'transform transition-all duration-300 ease-in-out'
  ),
  mobileVisible: 'translate-x-0 shadow-2xl',
  mobileHidden: '-translate-x-full shadow-none',
  // Backdrop for mobile menu
  backdrop: cx(
    'fixed inset-0 bg-black transition-opacity duration-300 ease-in-out'
  ),
  backdropVisible: 'opacity-50 z-40',
  backdropHidden: 'opacity-0 -z-10',
  // Header section
  header: cx('p-3 border-b', 'border-divider', 'flex items-center justify-between'),
  logo: 'font-semibold text-lg flex items-center gap-2',
  logoIcon: cx('h-5 w-5', 'text-accent'),
  // Close button for mobile
  closeButton: cx(
    'text-tertiary',
    'hover:text-primary',
    'p-1 rounded-full',
    animations.transition.default,
    animations.transform.rotate,
    animations.transform.hover
  ),
  // Navigation section
  nav: cx(
    'px-2 py-3',
    animations.transition.default
  ),
  navLink: buttons.navItem, // Use button navItem style
  // History section
  historySection: cx(
    'mt-3 px-3',
    animations.transition.default
  ),
  historyHeader: cx(
    typography.weight.medium,
    typography.size.tiny,
    'uppercase',
    'text-tertiary',
    'tracking-wider mb-2'
  ),
  historyList: 'overflow-auto max-h-[calc(100vh-18rem)]',
  historyItem: cx(
    'flex items-start gap-2 p-2 rounded-md',
    'transition-colors cursor-pointer group'
  ),
  historyItemActive: 'bg-hover', // Active nav item color
  historyItemInactive: 'hover:bg-component-light', // Hover state
  historyContent: 'flex items-start flex-grow',
  historyIcon: cx('h-4 w-4 mt-0.5 flex-shrink-0', 'text-tertiary'),
  historyTextContainer: 'flex-1 min-w-0 ml-2',
  historyTitle: cx('text-xs font-medium', 'text-secondary', 'truncate'),
  historyDate: cx('text-xs', 'text-disabled'),
  deleteButton: cx(
    'flex-shrink-0 p-1.5',
    'text-disabled',
    'hover:text-secondary', 
    'hover:bg-component-light',
    'rounded transition-all duration-200 hover:scale-110'
  ),
  // User section
  userSection: cx(
    'mt-auto border-t', 
    'border-divider', 
    'p-3',
    animations.transition.default
  ),
  userContainer: 'flex items-center gap-2',
  avatar: cx('h-8 w-8 rounded-full', 'bg-accent-main', 'flex items-center justify-center text-primary-inverse'),
  avatarText: 'text-sm font-medium',
  userInfo: 'flex-1',
  userName: 'text-sm font-medium',
  userPlan: cx('text-xs', 'text-disabled')
};

// Chat component styles
export const chat = {
  // Message containers
  messagesContainer: 'space-y-8', // Increased spacing between turns
  messageTurn: 'flex flex-col gap-4', // Container for a query+response pair
  
  // User Query Title
  queryTitle: cx(
    typography.styles.heading, // Use heading style for prominence
    'text-lg md:text-xl', // Adjust size slightly
    'mb-2' // Add some space below the query
  ),
  // Removed querySticky style
  
  // Processing progress indicator
  progressContainer: cx(
    layout.flex.col,
    'gap-1 items-center my-3'
  ),
  progressText: cx(
    typography.size.sm,
    'text-tertiary'
  ),
  progressTrack: cx(
    'w-64 h-1 bg-component',
    'rounded-full overflow-hidden'
  ),
  progressFill: cx(
    'bg-accent h-full',
    animations.transition.default
  ),

  // Bot Response Content
  botContent: 'w-full',
  botText: cx(
    'prose prose-sm max-w-none',
    'break-words whitespace-normal overflow-wrap',
    'overflow-auto'
  ),
  // Code block specific styles to ensure proper wrapping
  codeBlock: cx(
    'whitespace-pre-wrap',
    'overflow-x-auto',
    'max-w-full'
  ),
  
  // Copy button
  copyButtonContainer: cx(
    'absolute bottom-2 right-2', // Changed from top-2 to bottom-2
    'opacity-0 group-hover:opacity-100',
    'transition-opacity duration-200'
  ),
  copyButton: cx(
    'text-tertiary hover:text-secondary',
    'p-1 rounded-full',
    'bg-transparent hover:bg-component-light',
    'transition-colors duration-200',
    'flex items-center justify-center'
  ),
  copySuccess: cx(
    'text-xs text-accent',
    'animate-fade-in-out'
  ),
  
  // Citations
  citationsSection: cx('mt-4 border-t', 'border-main', 'pt-3'),
  citationsHeader: cx(
    'text-sm font-medium',
    'text-secondary',
    'mb-2'
  ),
  citationContainer: 'space-y-2',
  citation: cx('p-2', 'bg-component', 'rounded-md text-sm', 'text-secondary'),
  citationText: 'text-secondary',
  citationSource: cx(
    'text-accent',
    'hover:underline font-medium'
  ),
  
  // Related queries
  queriesSection: 'mt-4',
  queriesHeader: cx(
    'text-sm font-medium',
    'text-secondary',
    'mb-2'
  ),
  queriesContainer: 'flex flex-wrap gap-2',
  queryButton: cx(
    'px-3 py-1',
    'bg-component',
    'text-secondary',
    'rounded-full text-sm',
    'hover:bg-component-light',
    animations.transition.color
  ),
  
  // Loading indicator
  loadingContainer: cx(
    'flex items-center space-x-2',
    'bg-component',
    'border',
    'border-main',
    'rounded-lg p-4 shadow-sm',
    'max-w-[85%]'
  ),
  loadingDot: cx('bg-accent-light', 'rounded-full w-2 h-2 animate-bounce'),
  loadingText: cx(
    'text-tertiary',
    'ml-1'
  )
};

// Layout components
export const pageLayout = {
  // Common page container
  container: cx('min-h-screen w-full flex flex-col', 'bg-page', 'overflow-hidden'),
  
  // Scrollable content area
  content: cx(
    'w-full max-w-3xl mx-auto px-4 sm:px-6',
    'h-screen overflow-y-auto',
    'bg-page'
  ),
  
  // Landing page specific
  landingContent: 'pt-[38vh] md:pt-[32vh]',
  
  // Logo area
  logoContainer: 'text-center mb-5',
  logoText: cx('text-2xl md:text-3xl font-semibold', 'text-primary'),
  
  // Search container
  searchContainer: 'max-w-xl mx-auto w-full',
  
  // Topics section
  topicsSection: 'mt-6 mb-5 max-w-xl mx-auto',
  
  // Content grid section
  contentSection: 'mb-16 max-w-3xl mx-auto w-full'
};
